import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, CreditCard, Smartphone, Lock, Unlock, History, Printer, UserCheck, RefreshCw } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const CashRegister = ({ user }) => {
  const [currentRegister, setCurrentRegister] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [openFormData, setOpenFormData] = useState({ monto_inicial: '', usuario_asignado_id: '' });
  const [closeFormData, setCloseFormData] = useState({ total_real: '', notas: '', caja_id: '' });
  const [businessConfig, setBusinessConfig] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [onlyCashiersMode, setOnlyCashiersMode] = useState(false);
  const [allOpenRegisters, setAllOpenRegisters] = useState([]);
  const [selectedRegisterToClose, setSelectedRegisterToClose] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactionFormData, setTransactionFormData] = useState({
    tipo: 'INGRESO',
    monto: '',
    descripcion: ''
  });

  useEffect(() => {
    if (!user) return;
    
    fetchCurrentRegister();
    fetchHistory();
    fetchBusinessConfig();
    fetchAvailableUsers();
    fetchTransactions();
    // Solo admin/superadmin pueden ver todas las cajas
    if (user.role === 'admin' || user.role === 'superadmin') {
      fetchAllOpenRegisters();
    }
  }, [user]);

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración del negocio');
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await axios.get(`${API}/cash-register/available-users`);
      setAvailableUsers(response.data.users);
      setOnlyCashiersMode(response.data.only_cashiers_mode);
    } catch (error) {
      console.error('Error al cargar usuarios disponibles');
    }
  };

  const fetchAllOpenRegisters = async () => {
    try {
      const response = await axios.get(`${API}/cash-register/all-open`);
      setAllOpenRegisters(response.data);
    } catch (error) {
      console.error('Error al cargar cajas abiertas');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/cash-transactions/current`);
      setTransactions(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error al cargar movimientos');
      }
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cash-transactions`, {
        tipo: transactionFormData.tipo,
        monto: parseFloat(transactionFormData.monto),
        descripcion: transactionFormData.descripcion
      });
      toast.success(`${transactionFormData.tipo === 'INGRESO' ? 'Ingreso' : 'Egreso'} registrado exitosamente`);
      setTransactionDialogOpen(false);
      setTransactionFormData({ tipo: 'INGRESO', monto: '', descripcion: '' });
      fetchCurrentRegister();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const fetchCurrentRegister = async () => {
    try {
      const response = await axios.get(`${API}/cash-register/current`);
      setCurrentRegister(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        // toast.error('Error al cargar caja actual');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/cash-register/history`);
      setHistory(response.data.slice(0, 10));
    } catch (error) {
      // toast.error('Error al cargar historial');
    }
  };

  const [recalculatingId, setRecalculatingId] = useState(null);

  const handleRecalculate = async (reg) => {
    const confirmMsg = `¿Recalcular el cuadre de la caja de "${reg.cajero_nombre}"?\n\nSe descontarán las facturas anuladas que aún estén sumadas y se recalculará el Total Esperado (Monto Inicial + Ventas + movimientos de caja). Esta acción actualiza el registro y no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;
    setRecalculatingId(reg.id);
    try {
      const response = await axios.post(`${API}/cash-register/${reg.id}/recalculate`);
      toast.success(response.data?.message || 'Cuadre recalculado exitosamente');
      fetchHistory();
      fetchCurrentRegister();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular el cuadre');
    } finally {
      setRecalculatingId(null);
    }
  };

  const handleOpenRegister = async (e) => {
    e.preventDefault();
    
    if (!openFormData.usuario_asignado_id) {
      toast.error('Debe seleccionar un usuario responsable');
      return;
    }
    
    try {
      await axios.post(`${API}/cash-register/open`, {
        monto_inicial: parseFloat(openFormData.monto_inicial),
        usuario_asignado_id: openFormData.usuario_asignado_id,
      });
      toast.success('Caja abierta exitosamente');
      setOpenDialogOpen(false);
      setOpenFormData({ monto_inicial: '', usuario_asignado_id: '' });
      fetchCurrentRegister();
      fetchHistory();
      fetchAvailableUsers(); // Refrescar lista de usuarios
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir caja');
    }
  };

  const handleCloseRegister = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        total_real: parseFloat(closeFormData.total_real),
        notas: closeFormData.notas,
      };
      
      // Si es admin/superadmin y hay una caja seleccionada, incluir el ID
      if (user && (user.role === 'admin' || user.role === 'superadmin') && selectedRegisterToClose) {
        payload.caja_id = selectedRegisterToClose.id;
      }
      
      const response = await axios.post(`${API}/cash-register/close`, payload);
      
      const closedRegister = response.data;
      
      toast.success('Caja cerrada exitosamente');
      setCloseDialogOpen(false);
      setCloseFormData({ total_real: '', notas: '', caja_id: '' });
      setSelectedRegisterToClose(null);
      setCurrentRegister(null);
      fetchHistory();
      
      // Refrescar lista de cajas abiertas si es admin
      if (user && (user.role === 'admin' || user.role === 'superadmin')) {
        fetchAllOpenRegisters();
      }
      
      // Imprimir cierre automáticamente
      setTimeout(() => {
        printCashRegisterClose(closedRegister);
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    }
  };

  const printCashRegisterClose = async (register) => {
    // Usar el businessConfig que ya está cargado en el estado
    let config = businessConfig;
    if (!config) {
      try {
        // Usar el endpoint con autenticación
        const response = await axios.get(`${API}/business-config`);
        config = response.data;
      } catch (error) {
        console.error('Error al cargar configuración:', error);
        config = {}; // Usar objeto vacío si falla
      }
    }
    
    // Usar valores de config - priorizar nombre_comercio
    const printConfig = {
      logo: config?.logo || '',
      nombre_comercio: config?.nombre_comercio || 'MOTO REPUESTO CHAPULIN',
      sistema_nombre: config?.sistema_nombre || 'Sistema de Gestión',
      rnc: config?.rnc || '',
      telefono: config?.telefono || '',
      direccion: config?.direccion || '',
      slogan: config?.slogan || ''
    };
    
    console.log('BusinessConfig cargado:', config);
    console.log('Nombre comercio a usar:', printConfig.nombre_comercio);
    
    const printWindow = window.open('', '', 'width=280,height=600');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Cierre de Caja ${register.id?.slice(0,8)}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: 72mm auto;
              margin: 2mm;
            }
            
            @media print {
              body { 
                margin: 0 !important; 
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            body { 
              font-family: 'Arial', 'Helvetica', sans-serif; 
              font-size: 12px;
              line-height: 1.4;
              width: 68mm;
              margin: 0 auto;
              padding: 3mm;
              color: #000;
              background: #fff;
            }
            
            .header { 
              text-align: center; 
              border-bottom: 2px dashed #000; 
              padding-bottom: 3mm; 
              margin-bottom: 3mm; 
            }
            
            h2 { 
              margin: 2mm 0; 
              font-size: 16px; 
              font-weight: bold;
              letter-spacing: 0.5px;
            }
            
            h3 { 
              margin: 2mm 0; 
              font-size: 14px; 
              font-weight: bold;
            }
            
            p { 
              margin: 1mm 0; 
              font-size: 11px;
              line-height: 1.3;
            }
            
            .info { 
              margin-bottom: 3mm; 
              font-size: 11px;
              line-height: 1.4;
            }
            
            .info-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 1mm 0;
              font-size: 11px;
              line-height: 1.4;
            }
            
            .separator { 
              border-top: 1px dashed #000; 
              margin: 3mm 0; 
            }
            
            .totals { 
              margin-top: 3mm; 
              border-top: 2px solid #000; 
              padding-top: 3mm; 
            }
            
            .total-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 1.5mm 0; 
              font-size: 12px;
              line-height: 1.4;
            }
            
            .grand-total { 
              font-weight: bold; 
              font-size: 15px; 
              border-top: 2px solid #000; 
              padding-top: 3mm; 
              margin-top: 2mm; 
            }

            .highlight-box {
              border: 2px solid #000;
              padding: 2mm;
              margin: 2mm 0;
              font-weight: bold;
            }
            
            .footer { 
              text-align: center; 
              margin-top: 4mm; 
              font-size: 10px; 
              border-top: 1px dashed #000; 
              padding-top: 3mm; 
              line-height: 1.4;
            }
            
            .bold { 
              font-weight: bold; 
            }

            .small { 
              font-size: 10px; 
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${printConfig.logo ? `<img src="${printConfig.logo}" alt="Logo" style="max-width: 45mm; max-height: 18mm; margin: 0 auto 2mm; display: block;" />` : ''}
            <h2 style="font-size: 18px; margin: 2mm 0;">${printConfig.nombre_comercio}</h2>
            ${printConfig.slogan ? `<p class="small">${printConfig.slogan}</p>` : ''}
            ${printConfig.rnc ? `<p>RNC: ${printConfig.rnc}</p>` : ''}
            ${printConfig.telefono ? `<p>Tel: ${printConfig.telefono}</p>` : ''}
            ${printConfig.direccion ? `<p class="small">${printConfig.direccion}</p>` : ''}
            <h3 style="margin-top: 3mm; font-size: 15px;">CIERRE DE CAJA</h3>
          </div>
          
          <div class="info">
            <div class="info-row"><span>Cajero:</span><span class="bold">${register.cajero_nombre}</span></div>
            <div class="info-row"><span>Turno:</span><span>${register.id?.slice(0,8)}</span></div>
          </div>

          <div class="separator"></div>

          <div class="info">
            <div class="info-row"><span>Apertura:</span><span>${new Date(register.fecha_apertura).toLocaleString('es-DO', {
              year: 'numeric', month: '2-digit', day: '2-digit', 
              hour: '2-digit', minute: '2-digit'
            })}</span></div>
            <div class="info-row"><span>Cierre:</span><span>${new Date(register.fecha_cierre).toLocaleString('es-DO', {
              year: 'numeric', month: '2-digit', day: '2-digit', 
              hour: '2-digit', minute: '2-digit'
            })}</span></div>
          </div>

          <div class="separator"></div>

          <div class="totals">
            <div class="total-row">
              <span>Monto Inicial:</span>
              <span class="bold">RD$ ${register.monto_inicial.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            <div class="total-row">
              <span>Total Ventas:</span>
              <span class="bold">RD$ ${register.total_ventas.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            <div class="total-row">
              <span>Total Esperado:</span>
              <span class="bold">RD$ ${register.total_esperado.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            ${register.costo_total !== undefined && register.costo_total !== null ? `
            <div class="total-row">
              <span>Costo Total:</span>
              <span class="bold">RD$ ${(register.costo_total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            ` : ''}
            <div class="total-row" style="color: #16a34a; font-size: 14px; border-top: 1px dashed #000; padding-top: 2mm; margin-top: 2mm;">
              <span>GANANCIA DEL DÍA:</span>
              <span class="bold">RD$ ${(register.ganancia || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
          </div>

          <div class="separator"></div>

          <div class="highlight-box">
            <div class="total-row">
              <span>Total Real:</span>
              <span>RD$ ${register.total_real.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
          </div>

          <div class="highlight-box" style="${register.diferencia === 0 ? 'border-color: #16a34a;' : register.diferencia > 0 ? 'border-color: #2563eb;' : 'border-color: #dc2626;'}">
            <div class="total-row">
              <span>Diferencia:</span>
              <span style="${register.diferencia === 0 ? 'color: #16a34a;' : register.diferencia > 0 ? 'color: #2563eb;' : 'color: #dc2626;'}">
                RD$ ${register.diferencia.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                ${register.diferencia === 0 ? ' (CUADRADO)' : register.diferencia > 0 ? ' (SOBRANTE)' : ' (FALTANTE)'}
              </span>
            </div>
          </div>

          ${register.notas ? `
            <div class="separator"></div>
            <div class="info">
              <div class="bold">Notas:</div>
              <p style="margin-top: 1mm;">${register.notas}</p>
            </div>
          ` : ''}

          <div class="footer">
            <p style="font-size: 11px;">${printConfig.nombre_comercio}</p>
            <p class="small" style="margin-top: 2mm;">Documento generado el ${new Date().toLocaleString('es-DO')}</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-register-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Caja</h1>
          <p className="text-slate-600 mt-1">Gestión de apertura y cierre de caja</p>
        </div>

        {!currentRegister && (
          <Dialog open={openDialogOpen} onOpenChange={(open) => {
            setOpenDialogOpen(open);
            if (open && user?.role === 'cajero') {
              setOpenFormData(prev => ({ ...prev, usuario_asignado_id: user.id }));
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="open-cash-register-btn">
                <Unlock className="mr-2" size={18} />
                Abrir Caja
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="open-cash-register-dialog">
              <DialogHeader>
                <DialogTitle>Abrir Caja</DialogTitle>
                <DialogDescription>Ingrese el monto inicial de efectivo en caja</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleOpenRegister} className="space-y-4">
                {user?.role === 'cajero' ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-sm text-emerald-800">
                      <UserCheck className="inline mr-1" size={14} />
                      Caja asignada a: <strong>{user.full_name}</strong>
                    </p>
                  </div>
                ) : (
                  <>
                    {onlyCashiersMode && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-800">
                          <strong>Modo Cajeros:</strong> Solo usuarios con rol Cajero pueden ser asignados a una caja.
                        </p>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="usuario_asignado">Usuario Responsable *</Label>
                      <Select 
                        value={openFormData.usuario_asignado_id} 
                        onValueChange={(value) => setOpenFormData({ ...openFormData, usuario_asignado_id: value })}
                        required
                      >
                        <SelectTrigger data-testid="form-usuario-select">
                          <SelectValue placeholder="Seleccionar usuario..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableUsers.map((u) => (
                            <SelectItem 
                              key={u.id} 
                              value={u.id}
                              disabled={u.tiene_caja_abierta}
                            >
                              <div className="flex items-center gap-2">
                                <span>{u.full_name}</span>
                                <span className="text-xs text-slate-500">({u.role})</span>
                                {u.tiene_caja_abierta && (
                                  <span className="text-xs text-red-600 font-semibold">- Caja abierta</span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                          {availableUsers.length === 0 && (
                            <SelectItem value="empty" disabled>
                              No hay usuarios disponibles
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        <UserCheck className="inline" size={12} /> Usuario que será responsable de esta caja
                      </p>
                    </div>
                  </>
                )}
                
                <div>
                  <Label htmlFor="monto_inicial">Monto Inicial (RD$) *</Label>
                  <Input
                    id="monto_inicial"
                    type="number"
                    step="0.01"
                    value={openFormData.monto_inicial}
                    onChange={(e) => setOpenFormData({ ...openFormData, monto_inicial: e.target.value })}
                    required
                    placeholder="0.00"
                    data-testid="form-monto-inicial-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="submit-open-register-btn">
                  Abrir Caja
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {currentRegister && (
          <div className="flex gap-2">
            <Dialog open={transactionDialogOpen} onOpenChange={setTransactionDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-500 hover:bg-blue-600">
                  <DollarSign className="mr-2" size={18} />
                  Registrar Movimiento
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Registrar Ingreso/Egreso</DialogTitle>
                  <DialogDescription>Registre movimientos de efectivo en la caja</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTransaction} className="space-y-4">
                  <div>
                    <Label htmlFor="tipo">Tipo de Movimiento *</Label>
                    <Select 
                      value={transactionFormData.tipo} 
                      onValueChange={(value) => setTransactionFormData({ ...transactionFormData, tipo: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INGRESO">
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-semibold">↑</span>
                            <span>Ingreso - Aumenta el efectivo</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="EGRESO">
                          <div className="flex items-center gap-2">
                            <span className="text-red-600 font-semibold">↓</span>
                            <span>Egreso - Disminuye el efectivo</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="monto">Monto (RD$) *</Label>
                    <Input
                      id="monto"
                      type="number"
                      step="0.01"
                      value={transactionFormData.monto}
                      onChange={(e) => setTransactionFormData({ ...transactionFormData, monto: e.target.value })}
                      required
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="descripcion">Descripción/Motivo *</Label>
                    <Textarea
                      id="descripcion"
                      value={transactionFormData.descripcion}
                      onChange={(e) => setTransactionFormData({ ...transactionFormData, descripcion: e.target.value })}
                      required
                      rows={3}
                      placeholder="Ejemplo: Pago a proveedor, Cambio de billetes, etc."
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className={`w-full ${transactionFormData.tipo === 'INGRESO' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
                  >
                    Registrar {transactionFormData.tipo === 'INGRESO' ? 'Ingreso' : 'Egreso'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            
            <Button 
              className="bg-red-500 hover:bg-red-600" 
              data-testid="close-cash-register-btn"
              onClick={() => {
                setSelectedRegisterToClose(null);
                setCloseDialogOpen(true);
              }}
            >
              <Lock className="mr-2" size={18} />
              Cerrar Caja
            </Button>
          </div>
        )}
      </div>

      {/* Current Register Status */}
      {currentRegister ? (
        <div data-testid="current-cash-register">
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Unlock className="text-emerald-600" size={28} />
                Caja Abierta
              </CardTitle>
              <CardDescription>
                Apertura: {new Date(currentRegister.fecha_apertura).toLocaleString('es-DO')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Monto Inicial</span>
                    <DollarSign className="text-blue-500" size={20} />
                  </div>
                  <div className="text-2xl font-bold text-slate-800">
                    {formatCurrency(currentRegister.monto_inicial)}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Efectivo</span>
                    <DollarSign className="text-green-500" size={20} />
                  </div>
                  <div className="text-2xl font-bold text-slate-800">
                    {formatCurrency(currentRegister.ventas_efectivo)}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Tarjeta</span>
                    <CreditCard className="text-purple-500" size={20} />
                  </div>
                  <div className="text-2xl font-bold text-slate-800">
                    {formatCurrency(currentRegister.ventas_tarjeta)}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Transferencia</span>
                    <Smartphone className="text-orange-500" size={20} />
                  </div>
                  <div className="text-2xl font-bold text-slate-800">
                    {formatCurrency(currentRegister.ventas_transferencia)}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-white rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="text-emerald-600" size={24} />
                    <span className="text-lg font-semibold text-slate-700">Total Esperado:</span>
                  </div>
                  <div className="text-3xl font-bold text-emerald-600">
                    {formatCurrency(currentRegister.total_esperado)}
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-600">
                  Total Ventas: {formatCurrency(currentRegister.total_ventas)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="no-open-register">
          <Lock className="mx-auto text-slate-400 mb-4" size={48} />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No hay caja abierta</h3>
          <p className="text-slate-600 mb-4">Debe abrir una caja para comenzar a realizar ventas</p>
        </div>
      )}

      {/* Movimientos de Efectivo */}
      {currentRegister && transactions.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={24} className="text-blue-600" />
              Movimientos de Efectivo
            </CardTitle>
            <CardDescription>Ingresos y egresos registrados en esta caja</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {transactions.map((trans) => (
                <div 
                  key={trans.id} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    trans.tipo === 'INGRESO' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-lg ${
                        trans.tipo === 'INGRESO' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {trans.tipo === 'INGRESO' ? '↑' : '↓'}
                      </span>
                      <div>
                        <p className="font-medium text-slate-800">{trans.descripcion}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(trans.fecha).toLocaleString('es-DO')} • {trans.usuario_nombre}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`text-xl font-bold ${
                    trans.tipo === 'INGRESO' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {trans.tipo === 'INGRESO' ? '+' : '-'}{formatCurrency(trans.monto)}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-700">Balance Total de Movimientos:</span>
                <span className="text-xl font-bold text-blue-600">
                  {formatCurrency(
                    transactions.reduce((acc, t) => 
                      t.tipo === 'INGRESO' ? acc + t.monto : acc - t.monto, 
                    0)
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin: All Open Registers */}
      {user && (user.role === 'admin' || user.role === 'superadmin') && allOpenRegisters.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <UserCheck size={24} />
              Todas las Cajas Abiertas
            </CardTitle>
            <CardDescription>
              Puede cerrar cualquier caja desde aquí
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allOpenRegisters.map((register) => (
                <Card key={register.id} className="border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{register.cajero_nombre}</CardTitle>
                    <CardDescription className="text-xs">
                      Abierta: {new Date(register.fecha_apertura).toLocaleString('es-DO')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Monto Inicial:</span>
                      <span className="font-semibold">{formatCurrency(register.monto_inicial)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Total Ventas:</span>
                      <span className="font-semibold">{formatCurrency(register.total_ventas)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-2">
                      <span className="text-slate-700 font-medium">Total Esperado:</span>
                      <span className="font-bold text-green-600">{formatCurrency(register.total_esperado)}</span>
                    </div>
                    <Button
                      className="w-full mt-3 bg-red-500 hover:bg-red-600"
                      size="sm"
                      onClick={() => {
                        setSelectedRegisterToClose(register);
                        setCloseDialogOpen(true);
                      }}
                    >
                      <Lock className="mr-2" size={16} />
                      Cerrar esta caja
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Close Cash Register Dialog - rendered independently */}
      <Dialog open={closeDialogOpen} onOpenChange={(open) => {
        setCloseDialogOpen(open);
        if (!open) setSelectedRegisterToClose(null);
      }}>
        <DialogContent data-testid="close-cash-register-dialog">
          <DialogHeader>
            <DialogTitle>Cerrar Caja</DialogTitle>
            <DialogDescription>
              {selectedRegisterToClose 
                ? `Cerrando caja de: ${selectedRegisterToClose.cajero_nombre}`
                : 'Realice el arqueo y cierre de caja'}
            </DialogDescription>
          </DialogHeader>
          {selectedRegisterToClose && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 font-medium">
                Está cerrando la caja de otro usuario
              </p>
            </div>
          )}
          {(selectedRegisterToClose || currentRegister) && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="text-sm space-y-1">
                {selectedRegisterToClose && (
                  <div className="flex justify-between mb-2 pb-2 border-b">
                    <span className="font-medium">Cajero:</span>
                    <span className="font-semibold">{selectedRegisterToClose.cajero_nombre}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Monto Inicial:</span>
                  <span className="font-semibold">
                    {formatCurrency((selectedRegisterToClose || currentRegister).monto_inicial)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Ventas:</span>
                  <span className="font-semibold">
                    {formatCurrency((selectedRegisterToClose || currentRegister).total_ventas)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="font-bold">Total Esperado:</span>
                  <span className="font-bold text-green-600">
                    {formatCurrency((selectedRegisterToClose || currentRegister).total_esperado)}
                  </span>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleCloseRegister} className="space-y-4">
            <div>
              <Label htmlFor="total_real">Total Real Contado (RD$) *</Label>
              <Input
                id="total_real"
                type="number"
                step="0.01"
                value={closeFormData.total_real}
                onChange={(e) => setCloseFormData({ ...closeFormData, total_real: e.target.value })}
                required
                placeholder="0.00"
                data-testid="form-total-real-input"
              />
            </div>
            <div>
              <Label htmlFor="notas">Notas (Opcional)</Label>
              <Textarea
                id="notas"
                value={closeFormData.notas}
                onChange={(e) => setCloseFormData({ ...closeFormData, notas: e.target.value })}
                rows={3}
                placeholder="Observaciones del cierre..."
                data-testid="form-notas-input"
              />
            </div>
            <Button type="submit" className="w-full bg-red-500 hover:bg-red-600" data-testid="submit-close-register-btn">
              Cerrar Caja
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History size={24} />
            Historial de Caja
          </CardTitle>
          <CardDescription>Últimos 10 cierres de caja</CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No hay historial de caja</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>Cajero</th>
                    <th>Apertura</th>
                    <th>Cierre</th>
                    <th>Inicial</th>
                    <th>Ventas</th>
                    <th>Esperado</th>
                    <th>Real</th>
                    <th>Diferencia</th>
                    <th>Ganancia</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody data-testid="cash-register-history-body">
                  {history.map((reg) => (
                    <tr key={reg.id} data-testid={`history-row-${reg.id}`}>
                      <td className="font-medium">{reg.cajero_nombre}</td>
                      <td className="text-sm">{new Date(reg.fecha_apertura).toLocaleString('es-DO')}</td>
                      <td className="text-sm">
                        {reg.fecha_cierre ? new Date(reg.fecha_cierre).toLocaleString('es-DO') : '-'}
                      </td>
                      <td>{formatCurrency(reg.monto_inicial)}</td>
                      <td>{formatCurrency(reg.total_ventas)}</td>
                      <td className="font-semibold">{formatCurrency(reg.total_esperado)}</td>
                      <td>{reg.total_real !== null ? `${formatCurrency(reg.total_real)}` : '-'}</td>
                      <td>
                        {reg.diferencia !== null ? (
                          <span
                            className={`font-semibold ${
                              reg.diferencia === 0
                                ? 'text-green-600'
                                : reg.diferencia > 0
                                ? 'text-blue-600'
                                : 'text-red-600'
                            }`}
                          >
                            {formatCurrency(reg.diferencia)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(reg.ganancia || 0)}
                        </span>
                      </td>
                      <td>
                        {reg.fecha_cierre && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => printCashRegisterClose(reg)}
                            title="Reimprimir cierre"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`reprint-close-btn-${reg.id}`}
                          >
                            <Printer size={16} />
                          </Button>
                        )}
                        {user && user.role === 'superadmin' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRecalculate(reg)}
                            disabled={recalculatingId === reg.id}
                            title="Recalcular cuadre (descontar facturas anuladas)"
                            className="ml-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            data-testid={`recalculate-btn-${reg.id}`}
                          >
                            <RefreshCw size={16} className={recalculatingId === reg.id ? 'animate-spin' : ''} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CashRegister;
